import { useState, useId, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import { Input } from '@/components/base/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Select';
import XBoldIcon from '@/icons/XBold.svg?react';
import type { WelcomeModalContent, CategoryOption } from '@/types/service-directory';

/**
 * Props interface for ServiceDirectoryWelcomeModal component
 */
export interface ServiceDirectoryWelcomeModalProps {
  /** Modal content */
  content?: WelcomeModalContent;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Available category options for dropdown */
  categories: CategoryOption[];
  /** Callback when Find Services is clicked with selected values */
  onFindServices: (location: string, categoryId: string | null) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ServiceDirectoryWelcomeModal - First-visit modal for the Service Directory
 *
 * Introduces users to the Service Directory and allows them to set initial
 * filters (location and category) before exploring services.
 *
 * @example
 * ```tsx
 * import { ServiceDirectoryWelcomeModal } from '@/components';
 * import { mockWelcomeModalContent, mockCategories } from '@/components';
 *
 * <ServiceDirectoryWelcomeModal
 *   content={mockWelcomeModalContent}
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   categories={categories}
 *   onFindServices={(location, categoryId) => handleFilters(location, categoryId)}
 * />
 * ```
 */
export function ServiceDirectoryWelcomeModal({
  content,
  isOpen,
  onClose,
  categories,
  onFindServices,
  className,
}: ServiceDirectoryWelcomeModalProps) {
  const [location, setLocation] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const locationInputId = useId();
  const categorySelectId = useId();

  // Detect mobile breakpoint (below 800px) - internal for portaled modals
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 799px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };
    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  if (!content) {
    return null;
  }

  // Allow submission when location is filled and a category is selected (including "all")
  const isSubmitDisabled = !location.trim() || !selectedCategory;

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation(e.target.value);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
  };

  const handleFindServices = () => {
    if (isSubmitDisabled) return;
    // Pass null for "all categories" to indicate no category filter
    const categoryId = selectedCategory === 'all' ? null : selectedCategory;
    onFindServices(location.trim(), categoryId);
    // Reset form state after submission
    setLocation('');
    setSelectedCategory('');
  };

  const handleClose = () => {
    // Reset form state on close
    setLocation('');
    setSelectedCategory('');
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-[130]"
          data-testid="welcome-modal-overlay"
          onClick={handleClose}
        />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'bg-white rounded-[4px]',
            'border border-[var(--color-border-default)]',
            'flex flex-col gap-6',
            'z-[131]',
            'focus:outline-none',
            isMobile
              ? 'w-[calc(100vw-32px)] max-w-[396px] h-auto max-h-[90vh] overflow-y-auto pt-6 pr-4 pb-4 pl-4'
              : 'w-[569px] max-h-[min(534px,90vh)] overflow-y-auto pt-10 pr-10 pb-6 pl-10',
            className
          )}
          style={{ boxShadow: 'var(--shadow-card)' }}
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          data-testid="welcome-modal-content"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Exit Button */}
          <Dialog.Close
            className="absolute top-[22px] right-[22px] w-5 h-5 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer"
            aria-label={content.closeLabel}
            data-testid="welcome-modal-close"
          >
            <XBoldIcon className="w-full h-full text-black" aria-hidden="true" />
          </Dialog.Close>

          {/* Section 1: Title and Subtitle */}
          <div className="flex flex-col gap-1.5">
            <Dialog.Title
              id={titleId}
              className="m-0"
              style={{
                fontFamily: "'Source Sans Pro', sans-serif",
                fontWeight: 600,
                fontSize: '32px',
                lineHeight: '40px',
                color: 'var(--color-foreground)',
              }}
            >
              {content.title}
            </Dialog.Title>

            <Dialog.Description
              id={descriptionId}
              style={{
                fontFamily: "'Source Sans Pro', sans-serif",
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: 'var(--color-foreground)',
              }}
            >
              {content.subtitle}
            </Dialog.Description>
          </div>

          {/* Section 2: Location Input */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor={locationInputId}
              className={typography.buttonLarge}
            >
              {content.locationInput.label}
            </label>
            <Input
              id={locationInputId}
              type="text"
              placeholder={content.locationInput.placeholder}
              value={location}
              onChange={handleLocationChange}
              className="w-full input-service-directory"
              data-testid="welcome-modal-location-input"
            />
          </div>

          {/* Section 3: Service Category Dropdown */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor={categorySelectId}
              className={typography.buttonLarge}
            >
              {content.categoryInput.label}
            </label>
            <Select
              value={selectedCategory || ''}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger
                id={categorySelectId}
                className="w-full input-service-directory"
                aria-label={content.categoryInput.label}
                data-testid="welcome-modal-category-trigger"
              >
                <SelectValue placeholder={content.categoryInput.placeholder} />
              </SelectTrigger>
              <SelectContent className="z-[200]">
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Section 4: Find Services Button */}
          <Button
            variant="primary"
            size="large"
            content={{ text: content.findServicesButton.text }}
            onClick={handleFindServices}
            disabled={isSubmitDisabled}
            aria-disabled={isSubmitDisabled}
            className="self-start mt-auto shrink-0"
            data-testid="welcome-modal-find-services-button"
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

ServiceDirectoryWelcomeModal.displayName = 'ServiceDirectoryWelcomeModal';
